import React from "react";
import { Link } from "react-router-dom";
import { Star, Clock, MapPin, Heart } from "lucide-react";
import { formatPrice } from "@/lib/currency";

const CATEGORY_COLORS = {
  Rides: "text-orange-400 border-orange-400/30",
  Tours: "text-cyan-300 border-cyan-300/30",
  Activities: "text-fuchsia-400 border-fuchsia-400/30",
  Transfers: "text-emerald-400 border-emerald-400/30",
};

export default function ListingCard({ listing, onFav, isFav }) {
  const priceLabel = listing.price_type === "flat" ? "flat rate" : "per person";
  return (
    <Link
      to={`/listing/${listing.id}`}
      data-testid={`listing-card-${listing.id}`}
      className="group glass glass-hover rounded-2xl overflow-hidden flex flex-col"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={listing.image_url || listing.images?.[0]}
          alt={listing.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#070A18] via-transparent to-transparent opacity-70" />
        <span className={`absolute top-3 left-3 text-[10px] font-mono-caption uppercase tracking-widest px-3 py-1 rounded-full glass border ${CATEGORY_COLORS[listing.category] || "text-orange-400 border-orange-400/30"}`}>
          {listing.category}
        </span>
        {onFav && (
          <button
            data-testid={`fav-btn-${listing.id}`}
            onClick={(e) => { e.preventDefault(); onFav(listing.id); }}
            className="absolute top-3 right-3 w-9 h-9 rounded-full glass flex items-center justify-center text-white hover:text-[#FF4D4D] transition-colors"
          >
            <Heart className={`w-4 h-4 ${isFav ? "fill-[#FF4D4D] text-[#FF4D4D]" : ""}`} />
          </button>
        )}
        <div className="absolute bottom-3 left-3 flex items-center gap-1.5 glass rounded-full px-3 py-1">
          <Star className="w-3.5 h-3.5 fill-[#22E67A] text-[#22E67A]" />
          <span className="text-white text-xs font-bold">{listing.rating?.toFixed(1)}</span>
          <span className="text-slate-400 text-[11px]">({listing.reviews_count})</span>
        </div>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-heading font-extrabold text-white text-xl leading-tight mb-3 group-hover:text-gradient-warm transition-colors line-clamp-2">
          {listing.title}
        </h3>
        <div className="flex flex-col gap-1.5 mb-4 flex-1">
          <div className="flex items-center gap-1.5 text-xs text-slate-400">
            <Clock className="w-3.5 h-3.5 text-orange-400 shrink-0" />
            <span data-testid={`card-duration-${listing.id}`}>{listing.duration || "Flexible"}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-400">
            <MapPin className="w-3.5 h-3.5 text-cyan-300 shrink-0" />
            <span className="line-clamp-1" data-testid={`card-meeting-${listing.id}`}>{listing.meeting_point || listing.location || listing.island}</span>
          </div>
        </div>
        <div className="flex items-end justify-between pt-3 border-t border-[#232A4D]">
          <div className="flex items-center gap-1 text-xs text-slate-400">
            <Star className="w-4 h-4 fill-[#22E67A] text-[#22E67A]" />
            <span className="rating-green font-bold">{listing.rating?.toFixed(1)}</span>
            <span className="text-slate-500">· {listing.reviews_count} reviews</span>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono-caption">From</div>
            <span className="font-heading font-extrabold price-red text-2xl leading-none">{formatPrice(listing.price, listing.currency)}</span>
            <div className="text-slate-500 text-[11px]">{priceLabel}</div>
          </div>
        </div>
      </div>
    </Link>
  );
}
